import type { CardDef, CardId } from './types';

/**
 * The starter deck. Magnitudes marked with `{n}` scale with escalation.
 * Hold cards are played by the owner on their turn with a target where noted.
 */
export const CARDS: readonly CardDef[] = [
  {
    id: 'cleanup-crew',
    name: 'Cleanup Crew',
    kind: 'hold',
    polarity: 'good',
    copies: 5,
    description: 'Remove {n} zombies from one territory you own.',
  },
  {
    id: 'airstrike',
    name: 'Airstrike',
    kind: 'hold',
    polarity: 'good',
    copies: 4,
    description: 'Remove {n} zombies from any territory adjacent to one you own.',
  },
  {
    id: 'antidote',
    name: 'Antidote',
    kind: 'hold',
    polarity: 'good',
    copies: 3,
    description: 'One territory you own is immune to bites for 2 rounds.',
  },
  {
    id: 'quarantine',
    name: 'Quarantine',
    kind: 'instant',
    polarity: 'good',
    copies: 4,
    description: 'No zombies spawn anywhere until the end of next round.',
  },
  {
    id: 'reinforcements',
    name: 'Reinforcements',
    kind: 'instant',
    polarity: 'good',
    copies: 6,
    description: 'Place {n} extra units now.',
  },
  {
    id: 'outbreak',
    name: 'Outbreak',
    kind: 'instant',
    polarity: 'bad',
    copies: 5,
    description: 'Zombies double in one random territory you own (up to {n} more).',
  },
  {
    id: 'contaminated-supplies',
    name: 'Contaminated Supplies',
    kind: 'instant',
    polarity: 'bad',
    copies: 4,
    description: 'Lose {n} units in your largest territory.',
  },
  {
    id: 'migration',
    name: 'Migration',
    kind: 'instant',
    polarity: 'bad',
    copies: 4,
    description: 'All zombies in a random territory next to yours shamble into it.',
  },
  {
    id: 'mutation',
    name: 'Mutation',
    kind: 'instant',
    polarity: 'bad',
    copies: 3,
    description: 'Zombies spawn more readily everywhere until the end of next round.',
  },
  {
    id: 'siren',
    name: 'Siren',
    kind: 'instant',
    polarity: 'bad',
    copies: 4,
    description: 'Your territories roll for zombies twice at your next Rising.',
  },
];

export const CARD_BY_ID: Readonly<Record<CardId, CardDef>> = Object.fromEntries(
  CARDS.map((c) => [c.id, c]),
);

/** Base magnitudes before escalation. */
export const CARD_MAGNITUDE: Readonly<Record<string, number>> = {
  'cleanup-crew': 5,
  airstrike: 3,
  reinforcements: 3,
  'contaminated-supplies': 2,
  /** Outbreak doubles, but never adds more than this times the multiplier. */
  outbreak: 4,
};

export const ANTIDOTE_ROUNDS = 2;

/** Cards that need a territory target when played. */
export const TARGETED_CARDS: ReadonlySet<CardId> = new Set([
  'cleanup-crew',
  'airstrike',
  'antidote',
]);

/** A fresh, unshuffled deck. */
export function buildDeck(cards: readonly CardDef[] = CARDS): CardId[] {
  return cards.flatMap((c) => Array.from({ length: c.copies }, () => c.id));
}
